import {useRef} from 'react';
import cx from 'classnames';
import Link from '@/links';
import useOnClickOutside from '@/hooks/useClickOutside';
import type {NavDropdownProps} from '../../types/components/header';
import styles from './nav-dropdown.module.scss';

const DROPDOWN_ARROW_SRC = '/static/images/ui/dropdown-arrow-green.svg';

const NavDropdown = ({
  id,
  label,
  items,
  triggerClassName,
  isOpen,
  isItemCurrent = () => false,
  closeOnOutsideClick = true,
  onToggle,
  onNavigate
}: NavDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const isCurrent = items.some(child => isItemCurrent(child.href));

  useOnClickOutside(dropdownRef, () => {
    if (closeOnOutsideClick && isOpen) {
      onToggle();
    }
  });

  return (
    <div ref={dropdownRef} className={styles['nav-dropdown']}>
      <Link
        variant="cta-default-no-arrow"
        href="#"
        className={triggerClassName}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
        customAttributes={{
          'data-testid': `nav-link-${id}`,
          'aria-expanded': isOpen,
          ...(isCurrent && !isOpen ? {'aria-current': 'page'} : {})
        }}>
        <span className={styles['nav-dropdown-trigger']}>
          {label}
          <span
            className={cx(styles['nav-dropdown-arrow'], isOpen && styles['nav-dropdown-arrow--open'])}
            aria-hidden>
            <img src={DROPDOWN_ARROW_SRC} alt="" width={11} height={8} />
          </span>
        </span>
      </Link>

      <ul
        className={cx(
          styles['nav-dropdown-panel'],
          isOpen && styles['nav-dropdown-panel--open']
        )}
        aria-hidden={!isOpen}>
        {items.map(child => {
          const isChildCurrent = isItemCurrent(child.href);

          return (
            <li key={child.id}>
              <Link
                href={child.href}
                target={child.target}
                onClick={onNavigate}
                className={cx(
                  styles['nav-dropdown-panel-link'],
                  isChildCurrent && styles['nav-dropdown-panel-link--current']
                )}
                customAttributes={{
                  'data-testid': `nav-link-${id}-${child.id}`,
                  ...(isChildCurrent ? {'aria-current': 'page'} : {}),
                  ...(isOpen ? {} : {tabIndex: -1})
                }}>
                {child.text}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default NavDropdown;
